/**
 * Утилита для chunked upload (загрузка файлов по частям)
 * Поддерживает параллельную загрузку чанков, возобновление при обрыве и retry
 * Автор: AI Market Hub
 * Оптимизировано: Декабрь 2024
 */

import axios from 'axios';
import { 
  getUploadProgress, 
  saveUploadProgress, 
  clearUploadProgress,
  generateFileId 
} from './uploadValidation.js';

// ============= ОПТИМИЗИРОВАННЫЕ КОНСТАНТЫ =============
const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB - меньше HTTP запросов, быстрее загрузка
const MAX_PARALLEL_CHUNKS = 5; // 5 параллельных чанков - лучшая утилизация канала
const MAX_RETRIES = 3; // Количество повторных попыток при сбое
const RETRY_DELAY = 2000; // Базовая задержка между попытками (мс)
const CHUNK_TIMEOUT = 5 * 60 * 1000; // 5 минут на чанк 25MB
const MERGE_TIMEOUT = 10 * 60 * 1000; // 10 минут на объединение (для 5GB файлов)

/**
 * Разбивает файл на чанки
 * @param {File} file - Файл для разбиения
 * @param {number} chunkSize - Размер чанка в байтах
 * @returns {Array<{index: number, start: number, end: number, blob: Blob}>} Массив чанков
 */
export function splitFileIntoChunks(file, chunkSize = CHUNK_SIZE) {
  const chunks = [];
  let start = 0;
  let index = 0;
  
  while (start < file.size) {
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);
    chunks.push({
      index,
      start,
      end,
      blob,
      size: blob.size
    });
    start = end;
    index++;
  }
  
  console.log(`[ChunkedUpload] Файл ${file.name} разбит на ${chunks.length} чанков по ~${(chunkSize / 1024 / 1024).toFixed(0)}MB`);
  return chunks;
}

/**
 * Загружает один чанк файла (базовая функция)
 * @param {string} uploadId - Уникальный ID загрузки
 * @param {Object} chunk - Объект чанка
 * @param {string} fileName - Имя файла
 * @param {number} totalChunks - Общее количество чанков
 * @param {string} apiUrl - URL API
 * @param {Function} onProgress - Callback для обновления прогресса
 * @param {string} authToken - Токен авторизации
 * @returns {Promise<Object>} Результат загрузки чанка
 */
async function uploadChunk(uploadId, chunk, fileName, totalChunks, apiUrl, onProgress, authToken) {
  const formData = new FormData();
  // ВАЖНО: Поля идут ДО файла, чтобы backend мог сразу сохранять stream
  formData.append('uploadId', uploadId);
  formData.append('chunkIndex', chunk.index.toString());
  formData.append('fileName', fileName);
  formData.append('totalChunks', totalChunks.toString());
  formData.append('chunk', chunk.blob, `chunk_${chunk.index}`);
  
  // Создаем заголовки (НЕ указываем Content-Type - axios сделает это автоматически для FormData)
  const headers = {};
  
  // Добавляем токен авторизации
  // Если токен похож на JWT (содержит точки), используем Authorization: Bearer
  // Иначе используем x-session-token (для старой системы)
  if (authToken) {
    if (authToken.includes('.')) {
      // JWT токен (Supabase)
      headers['Authorization'] = `Bearer ${authToken}`;
    } else {
      // Старый session_token
      headers['x-session-token'] = authToken;
    }
  }
  
  const response = await axios.post(`${apiUrl}/youtube/upload-chunk`, formData, {
    headers,
    // axios автоматически установит правильный Content-Type с boundary для FormData
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100;
        onProgress({
          chunkIndex: chunk.index,
          chunkProgress,
          loaded: progressEvent.loaded,
          total: progressEvent.total
        });
      }
    },
    timeout: CHUNK_TIMEOUT
  });
  
  return { success: true, chunkIndex: chunk.index, data: response.data };
}

/**
 * Загружает чанк с автоматическими повторными попытками при сбое
 * @param {string} uploadId - Уникальный ID загрузки
 * @param {Object} chunk - Объект чанка
 * @param {string} fileName - Имя файла
 * @param {number} totalChunks - Общее количество чанков
 * @param {string} apiUrl - URL API
 * @param {Function} onProgress - Callback для обновления прогресса
 * @param {string} authToken - Токен авторизации
 * @returns {Promise<Object>} Результат загрузки чанка
 */
async function uploadChunkWithRetry(uploadId, chunk, fileName, totalChunks, apiUrl, onProgress, authToken) {
  let lastError;
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await uploadChunk(uploadId, chunk, fileName, totalChunks, apiUrl, onProgress, authToken);
      
      if (result.success) {
        if (attempt > 1) {
          console.log(`[ChunkedUpload] ✅ Чанк ${chunk.index} загружен с ${attempt}-й попытки`);
        }
        return result;
      }
      
      // Если success: false, это тоже ошибка
      throw new Error(result.error || 'Неизвестная ошибка');
      
    } catch (error) {
      lastError = error;
      
      // Проверяем, стоит ли повторять (не повторяем при 4xx ошибках кроме 429)
      const status = error.response?.status;
      const isRetryable = !status || status >= 500 || status === 429 || status === 408;
      
      if (!isRetryable) {
        console.error(`[ChunkedUpload] ❌ Чанк ${chunk.index}: ошибка ${status}, повтор невозможен`);
        throw error;
      }
      
      if (attempt < MAX_RETRIES) {
        const delay = RETRY_DELAY * attempt; // Экспоненциальная задержка: 2с, 4с, 6с
        console.warn(`[ChunkedUpload] ⚠️ Чанк ${chunk.index}: попытка ${attempt}/${MAX_RETRIES} не удалась, повтор через ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        console.error(`[ChunkedUpload] ❌ Чанк ${chunk.index}: все ${MAX_RETRIES} попытки исчерпаны`);
      }
    }
  }
  
  // Все попытки исчерпаны
  return { 
    success: false, 
    chunkIndex: chunk.index, 
    error: lastError?.response?.data?.error || lastError?.message || 'Превышено количество попыток'
  };
}

/**
 * Загружает файл по частям (chunked upload)
 * @param {File} file - Файл для загрузки
 * @param {string} apiUrl - URL API
 * @param {Object} options - Опции загрузки
 * @param {Function} options.onProgress - Callback для обновления прогресса (progress, uploadedBytes, totalBytes, speed)
 * @param {Function} options.onChunkComplete - Callback при завершении чанка
 * @param {string} options.authToken - Токен авторизации
 * @param {Function} options.getAuthToken - Функция для получения актуального токена (для автообновления)
 * @param {boolean} options.resume - Возобновить загрузку если есть сохраненный прогресс
 * @returns {Promise<Object>} Результат загрузки
 */
export async function uploadFileInChunks(file, apiUrl, options = {}) {
  const {
    onProgress,
    onChunkComplete,
    authToken: initialAuthToken,
    getAuthToken, // Функция для получения актуального токена
    resume = true
  } = options;
  
  // Текущий токен (может обновляться)
  let authToken = initialAuthToken;
  
  // Функция для обновления токена перед батчем
  const refreshTokenIfNeeded = async () => {
    if (getAuthToken) {
      try {
        const newToken = await getAuthToken();
        if (newToken && newToken !== authToken) {
          authToken = newToken;
          console.log('[ChunkedUpload] 🔄 Токен авторизации обновлен');
        }
      } catch (e) {
        // Игнорируем ошибку, используем текущий токен
        console.warn('[ChunkedUpload] Не удалось обновить токен:', e.message);
      }
    }
  };
  
  const fileId = generateFileId(file);
  const uploadId = `${fileId}_${Date.now()}`;
  
  console.log(`[ChunkedUpload] 🚀 Начало загрузки: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
  
  // Разбиваем файл на чанки
  const chunks = splitFileIntoChunks(file);
  const totalChunks = chunks.length;
  
  // Загружаем сохраненный прогресс если нужно
  let uploadedChunks = new Set();
  if (resume) {
    const savedProgress = getUploadProgress(fileId);
    if (savedProgress && savedProgress.uploadedChunks) {
      uploadedChunks = new Set(savedProgress.uploadedChunks);
      console.log(`[ChunkedUpload] ♻️ Возобновление загрузки: ${uploadedChunks.size}/${totalChunks} чанков уже загружено`);
    }
  }
  
  // Фильтруем уже загруженные чанки
  const chunksToUpload = chunks.filter(chunk => !uploadedChunks.has(chunk.index));
  
  if (chunksToUpload.length === 0) {
    // Все чанки уже загружены, переходим к объединению
    console.log('[ChunkedUpload] ✅ Все чанки уже загружены, объединяем файл...');
    return await mergeChunks(uploadId, file.name, totalChunks, apiUrl, authToken);
  }
  
  // Отслеживание прогресса
  let uploadedBytes = Array.from(uploadedChunks).reduce((sum, idx) => {
    return sum + chunks[idx].size;
  }, 0);
  const totalBytes = file.size;
  const startTime = Date.now();
  let lastSaveTime = startTime;
  let lastSpeedCalcTime = startTime;
  let lastSpeedCalcBytes = uploadedBytes;
  let currentSpeed = 0;
  
  // Функция для обновления прогресса
  const updateProgress = (forceUpdate = false) => {
    const now = Date.now();
    
    // Рассчитываем скорость каждые 500мс для плавности
    const speedTimeDiff = (now - lastSpeedCalcTime) / 1000;
    if (speedTimeDiff >= 0.5 || forceUpdate) {
      const bytesDiff = uploadedBytes - lastSpeedCalcBytes;
      currentSpeed = speedTimeDiff > 0 ? bytesDiff / speedTimeDiff : currentSpeed;
      lastSpeedCalcTime = now;
      lastSpeedCalcBytes = uploadedBytes;
    }
    
    const progress = (uploadedBytes / totalBytes) * 100;
    
    // Всегда вызываем onProgress для плавного обновления UI
    if (onProgress) {
      onProgress(progress, uploadedBytes, totalBytes, currentSpeed);
    }
    
    // Сохраняем прогресс в localStorage каждые 2 секунды (для возобновления)
    if (now - lastSaveTime > 2000 || forceUpdate) {
      saveUploadProgress(fileId, {
        uploadId,
        fileName: file.name,
        totalChunks,
        uploadedChunks: Array.from(uploadedChunks),
        uploadedBytes,
        totalBytes,
        progress
      });
      lastSaveTime = now;
    }
  };
  
  // Начальное обновление прогресса
  updateProgress(true);
  
  // Загружаем чанки параллельно (батчами)
  let currentBatch = [];
  
  for (const chunk of chunksToUpload) {
    // Добавляем чанк в текущий батч
    currentBatch.push(chunk);
    
    // Если батч заполнен или это последний чанк, запускаем загрузку
    if (currentBatch.length >= MAX_PARALLEL_CHUNKS || chunk === chunksToUpload[chunksToUpload.length - 1]) {
      // Обновляем токен перед каждым батчем (для долгих загрузок)
      await refreshTokenIfNeeded();
      
      const batchPromises = currentBatch.map(batchChunk => {
        return uploadChunkWithRetry(uploadId, batchChunk, file.name, totalChunks, apiUrl, (chunkProgress) => {
          // Обновляем общий прогресс при каждом событии загрузки чанка
          updateProgress();
        }, authToken);
      });
      
      // Ждем завершения текущего батча перед следующим
      const batchResults = await Promise.allSettled(batchPromises);
      
      // Обрабатываем результаты батча
      for (const result of batchResults) {
        if (result.status === 'fulfilled' && result.value.success) {
          uploadedChunks.add(result.value.chunkIndex);
          // Пересчитываем uploadedBytes на основе всех загруженных чанков
          uploadedBytes = Array.from(uploadedChunks).reduce((sum, idx) => {
            return sum + chunks[idx].size;
          }, 0);
          
          if (onChunkComplete) {
            onChunkComplete(result.value.chunkIndex, totalChunks);
          }
        } else {
          // Ошибка загрузки чанка (даже после retry)
          const error = result.status === 'fulfilled' 
            ? result.value.error 
            : result.reason?.message || 'Неизвестная ошибка';
          
          const chunkIndex = result.status === 'fulfilled' 
            ? result.value.chunkIndex 
            : 'unknown';
          
          console.error(`[ChunkedUpload] ❌ Критическая ошибка чанка ${chunkIndex}:`, error);
          
          // Сохраняем прогресс перед выбросом ошибки (для возобновления)
          updateProgress(true);
          
          throw new Error(`Ошибка загрузки чанка ${chunkIndex}: ${error}`);
        }
      }
      
      // Обновляем прогресс после батча
      uploadedBytes = Array.from(uploadedChunks).reduce((sum, idx) => {
        return sum + chunks[idx].size;
      }, 0);
      updateProgress(true);
      
      // Очищаем батч
      currentBatch = [];
    }
  }
  
  // Финальное обновление прогресса
  updateProgress(true);
  
  // Проверяем, все ли чанки загружены
  if (uploadedChunks.size !== totalChunks) {
    const error = new Error(`Не все чанки загружены: ${uploadedChunks.size}/${totalChunks}`);
    error.uploadedChunks = Array.from(uploadedChunks);
    error.totalChunks = totalChunks;
    throw error;
  }
  
  // Все чанки загружены, объединяем файл
  const uploadTime = ((Date.now() - startTime) / 1000).toFixed(1);
  const avgSpeed = (totalBytes / (Date.now() - startTime) * 1000 / 1024 / 1024).toFixed(1);
  console.log(`[ChunkedUpload] ✅ Все ${totalChunks} чанков загружены за ${uploadTime}с (средняя скорость: ${avgSpeed} MB/s)`);
  console.log('[ChunkedUpload] 🔄 Объединяем файл на сервере...');
  
  // Обновляем токен перед merge (на случай если истек)
  await refreshTokenIfNeeded();
  
  const mergeResult = await mergeChunks(uploadId, file.name, totalChunks, apiUrl, authToken);
  
  // Очищаем сохраненный прогресс после успешной загрузки
  clearUploadProgress(fileId);
  
  console.log(`[ChunkedUpload] 🎉 Загрузка завершена: ${file.name}`);
  
  return mergeResult;
}

/**
 * Объединяет загруженные чанки в один файл на сервере
 * @param {string} uploadId - Уникальный ID загрузки
 * @param {string} fileName - Имя файла
 * @param {number} totalChunks - Общее количество чанков
 * @param {string} apiUrl - URL API
 * @param {string} authToken - Токен авторизации
 * @returns {Promise<Object>} Результат объединения
 */
async function mergeChunks(uploadId, fileName, totalChunks, apiUrl, authToken) {
  try {
    // Создаем заголовки
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Добавляем токен авторизации
    // Если токен похож на JWT (содержит точки), используем Authorization: Bearer
    // Иначе используем x-session-token (для старой системы)
    if (authToken) {
      if (authToken.includes('.')) {
        // JWT токен (Supabase)
        headers['Authorization'] = `Bearer ${authToken}`;
      } else {
        // Старый session_token
        headers['x-session-token'] = authToken;
      }
    }
    
    const response = await axios.post(`${apiUrl}/youtube/merge-chunks`, {
      uploadId,
      fileName,
      totalChunks
    }, {
      headers,
      timeout: MERGE_TIMEOUT
    });
    
    return { success: true, video: response.data.video };
  } catch (error) {
    console.error('[ChunkedUpload] ❌ Ошибка объединения чанков:', error);
    throw new Error(error.response?.data?.error || error.message || 'Ошибка объединения чанков');
  }
}

/**
 * Отменяет загрузку и очищает сохраненный прогресс
 * @param {File} file - Файл
 */
export function cancelUpload(file) {
  const fileId = generateFileId(file);
  clearUploadProgress(fileId);
  console.log(`[ChunkedUpload] ❌ Загрузка отменена: ${file.name}`);
}

/**
 * Проверяет, есть ли незавершенная загрузка для файла
 * @param {File} file - Файл
 * @returns {Object|null} Информация о прогрессе или null
 */
export function getResumeInfo(file) {
  const fileId = generateFileId(file);
  return getUploadProgress(fileId);
}
